/**
 * LaTeX sitting in otherwise-plain content.
 *
 * The rich-text editor emits math as `<span class="math-tex" data-latex="…">`
 * and both renderers (web `RenderedHtml`, worksheet `prepareRichHtml`) run
 * KaTeX over exactly that markup. AI-generated questions never go through the
 * editor, so their math arrives one of two ways:
 *
 *   1. DELIMITED — `\(a_{10} + a_{12} = 25\)`, `\[ … \]`, `$$ … $$`.
 *   2. BARE — no delimiters at all: `Решите неравенство: \frac{5x+3}{x^2+x-2} > 1`.
 *
 * Both used to render literally. These helpers find each form so the renderers
 * can upgrade them to the same `.math-tex` markup the KaTeX pass already reads.
 * This is a RENDER-time upgrade, not a write-time migration: it also fixes
 * questions already sitting in the database, and it never touches stored
 * content, so there is no risk of double-escaping author-written HTML.
 *
 * Bare detection is a heuristic, so it is deliberately fenced in:
 *   - a run must contain a LaTeX control sequence, `^`, or `_` to start at all;
 *   - every token must pass an ASCII-math allowlist, which rejects Cyrillic and
 *     any other prose script outright;
 *   - word-shaped tokens can't extend a run, so `\alpha well-known` stops at
 *     `\alpha`;
 *   - `{{blank1}}` placeholders are excluded, so FILL_IN_BLANKS slots survive;
 *   - segments found this way are marked `auto`, and the renderers parse those
 *     with KaTeX's `throwOnError: true` so anything that isn't real LaTeX falls
 *     back to the original text rather than to a red error.
 *
 * Mirrored from `apps/api/src/common/raw-latex.ts` — the web bundler
 * cannot value-import workspace TS, so the two copies are kept in sync by hand
 * (same convention as the FEATURES/PERMISSIONS mirrors).
 *
 * `$ … $` is deliberately NOT a delimiter: single dollars collide with prices
 * ("the tool costs $5 and the upgrade $10") far more often than they mark math.
 */

/** Class the KaTeX passes select on. Matches what the editor's math node emits. */
export const MATH_CLASS = 'math-tex';

/** Added alongside `MATH_CLASS` for `\[…\]` / `$$…$$`, which render centered on their own line. */
export const MATH_DISPLAY_CLASS = 'math-display';

/** Marks a heuristically-detected segment, which the renderers parse strictly. */
export const MATH_AUTO_CLASS = 'math-auto';

export interface MathSegment {
  /** LaTeX source, delimiters stripped and normalized. */
  latex: string;
  /** True for `\[…\]` and `$$…$$`. */
  display: boolean;
  /** Index of the opening delimiter within the source string. */
  start: number;
  /** Index just past the closing delimiter. */
  end: number;
  /** Found without delimiters — render strictly, fall back to text on a parse error. */
  auto: boolean;
}

// Lazy content so `\(a\) and \(b\)` yields two segments rather than one that
// swallows the prose between them.
// `\\{1,2}` because a model writing JSON sometimes double-escapes its
// backslashes, so the parsed string carries `\\(x\\)` rather than `\(x\)`.
const DELIMITED_MATH =
  /\\{1,2}\(([\s\S]+?)\\{1,2}\)|\\{1,2}\[([\s\S]+?)\\{1,2}\]|\$\$([\s\S]+?)\$\$|\\{1,2}begin\{([a-zA-Z*]+)\}([\s\S]+?)\\{1,2}end\{\4\}|(?<![$\\])\$([^$\n]+?)\$(?!\$)/g;
const ANY_DELIMITER = /\\{1,2}\(|\\{1,2}\[|\$\$|\\{1,2}begin\{|\$/;

/** A control sequence, superscript or subscript — without one, a run is just prose. */
const MATH_TRIGGER = /[\\^_]/;

/** ASCII math plus the few Unicode operators models reach for. Rejects Cyrillic by omission. */
const MATH_SAFE = /^[A-Za-z0-9\\^_{}()[\]+\-*/=<>|!'".,:;%°~±×÷·−≤≥≠∞π√]+$/;

/** Grouping, scripts or commands — the shape of an expression rather than a word. */
const HAS_MATH_SYMBOL = /[(){}[\]^_\\]/;
const PURE_OPERATOR = /^[+\-*/=<>|·−×÷≤≥≠]+$/;
const WORD_LIKE = /[A-Za-z]{3,}/;
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;
const STRAY_DELIMITER = /\\[()[\]]|\$\$|\\begin\{|\\end\{/;

/** Unicode operators KaTeX can't parse, mapped to the commands it can. */
const UNICODE_OPERATORS: ReadonlyArray<readonly [RegExp, string]> = [
  [/·/g, '\\cdot '],
  [/×/g, '\\times '],
  [/÷/g, '\\div '],
  [/≤/g, '\\leq '],
  [/≥/g, '\\geq '],
  [/≠/g, '\\neq '],
  [/±/g, '\\pm '],
  [/∞/g, '\\infty '],
  [/−/g, '-'],
  [/π/g, '\\pi '],
  [/√/g, '\\sqrt '],
  [/[⎧⎪⎨⎩⎫⎬⎭╭╮╯╰│┤├]/g, ''],
];

/** Checks if text wrapped in single dollars represents real math rather than currency or prose. */
function isValidSingleDollarMath(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed) return false;
  // Markdown math convention: reject if padded with inner spaces to prevent colliding with prose
  if (content.startsWith(' ') || content.endsWith(' ')) return false;
  // Pure digits or currency numbers (e.g. "5", "5.00", "1,000") are currency, not math
  if (/^\d+(?:[.,]\d+)?$/.test(trimmed)) return false;
  // If it has spaces and contains long words (3+ letters) without any LaTeX commands or math symbols, reject
  if (/\s/.test(trimmed) && /[A-Za-z]{3,}/.test(trimmed) && !/[\\_^+\-*/=<>|]/.test(trimmed)) return false;
  return true;
}

/** Cheap pre-check so the common (math-free) case never runs the full scan. */
export function hasRawLatex(text: string): boolean {
  if (!text) return false;
  return ANY_DELIMITER.test(text) || MATH_TRIGGER.test(text);
}

export function findMathSegments(text: string): MathSegment[] {
  if (!hasRawLatex(text)) return [];

  const delimited = findDelimitedSegments(text);

  // Bare runs are only sought in the gaps: text already claimed by a delimiter
  // is the model's explicit LaTeX and must not be re-scanned.
  const segments: MathSegment[] = [];
  let cursor = 0;
  for (const segment of delimited) {
    segments.push(...findBareSegments(text, cursor, segment.start));
    segments.push(segment);
    cursor = segment.end;
  }
  segments.push(...findBareSegments(text, cursor, text.length));

  return segments;
}

/** `class` value for a segment, so both renderers agree on the markup. */
export function mathClassName(segment: MathSegment): string {
  const classes = [MATH_CLASS];
  if (segment.display) classes.push(MATH_DISPLAY_CLASS);
  if (segment.auto) classes.push(MATH_AUTO_CLASS);
  return classes.join(' ');
}

// ─── internals ──────────────────────────────────────────────────────────────

function findDelimitedSegments(text: string): MathSegment[] {
  const segments: MathSegment[] = [];
  // DELIMITED_MATH is module-level and /g, so it carries `lastIndex` between
  // calls. Reset before every scan.
  DELIMITED_MATH.lastIndex = 0;

  let match: RegExpExecArray | null = DELIMITED_MATH.exec(text);
  while (match !== null) {
    const [full, inline, bracket, dollars, envName, envBody, singleDollar] = match;

    if (singleDollar !== undefined) {
      if (isValidSingleDollarMath(singleDollar)) {
        let latex = singleDollar.trim();
        if (latex) {
          segments.push({
            latex: normalizeLatex(latex),
            display: false,
            start: match.index,
            end: match.index + full.length,
            auto: false,
          });
        }
      }
      match = DELIMITED_MATH.exec(text);
      continue;
    }

    if (envName !== undefined && envBody !== undefined) {
      let latex = full.trim();
      if (full.startsWith('\\\\')) {
        latex = latex.replace(/\\\\/g, '\\');
      }
      if (latex) {
        segments.push({
          latex: normalizeLatex(latex),
          display: true,
          start: match.index,
          end: match.index + full.length,
          auto: false,
        });
      }
      match = DELIMITED_MATH.exec(text);
      continue;
    }

    let latex = (inline ?? bracket ?? dollars ?? '').trim();
    // A doubled opening delimiter means the whole payload was escaped twice, so
    // the body reads `\\frac` where KaTeX needs `\frac`. Correctly-escaped
    // content can never produce a doubled delimiter, so this cannot eat a
    // legitimate `\\` line break.
    if (full.startsWith('\\\\')) {
      latex = latex.replace(/\\\\/g, '\\');
    }
    // `\(\)` carries nothing to render; leaving it as literal text is more
    // honest than emitting an empty math node.
    if (latex) {
      segments.push({
        latex: normalizeLatex(latex),
        display: inline === undefined,
        start: match.index,
        end: match.index + full.length,
        auto: false,
      });
    }
    match = DELIMITED_MATH.exec(text);
  }

  return segments;
}

interface Token {
  value: string;
  start: number;
  /** End of the token with trailing sentence punctuation excluded. */
  end: number;
}

/** Groups whitespace-separated math tokens into the longest runs that KaTeX could own. */
function findBareSegments(text: string, from: number, to: number): MathSegment[] {
  if (to - from < 2) return [];
  const slice = text.slice(from, to);
  if (!MATH_TRIGGER.test(slice)) return [];

  const tokens = tokenize(slice, from);
  const segments: MathSegment[] = [];

  let run: Token[] = [];
  let hasCore = false;

  const flush = () => {
    if (hasCore) {
      const trimmed = trimOperatorEdges(run);
      if (trimmed.length) {
        const start = trimmed[0].start;
        const end = trimmed[trimmed.length - 1].end;
        segments.push({
          latex: normalizeLatex(text.slice(start, end)),
          display: false,
          start,
          end,
          auto: true,
        });
      }
    }
    run = [];
    hasCore = false;
  };

  for (const token of tokens) {
    if (isMathCore(token.value)) {
      run.push(token);
      hasCore = true;
    } else if (run.length && canExtendRun(token.value)) {
      run.push(token);
    } else {
      flush();
      // A token that can't extend a run also can't start one — only a core can.
    }
  }
  flush();

  return segments;
}

function tokenize(slice: string, offset: number): Token[] {
  const tokens: Token[] = [];
  const pattern = /\S+/g;
  let match: RegExpExecArray | null = pattern.exec(slice);

  while (match !== null) {
    const raw = match[0];
    // Sentence punctuation belongs to the prose, not to the expression.
    const value = raw.replace(TRAILING_PUNCTUATION, '');
    if (value) {
      tokens.push({
        value,
        start: offset + match.index,
        end: offset + match.index + value.length,
      });
    }
    match = pattern.exec(slice);
  }

  return tokens;
}

/** A token that can start a run: real math shape, and carries a trigger. */
function isMathCore(value: string): boolean {
  return isSafe(value) && MATH_TRIGGER.test(value);
}

/**
 * A token that can continue a run but never start one. Word-shaped tokens are
 * refused so prose after an expression doesn't get swallowed.
 */
const SHORT_PROSE_WORDS = /^(is|in|at|on|by|to|of|or|if|an|as|no|so|do|my|he|we|it|be|up|am|me|us|go|ha|va|bu|va|da)$/i;

function canExtendRun(value: string): boolean {
  if (!isSafe(value)) return false;
  if (PURE_OPERATOR.test(value)) return true;
  if (HAS_MATH_SYMBOL.test(value)) return true;
  if (/^[A-Za-z]{1,2}$/.test(value) && !SHORT_PROSE_WORDS.test(value)) return true;
  return /\d/.test(value) && !WORD_LIKE.test(value);
}

function isSafe(value: string): boolean {
  // `{{blank1}}` placeholders are structural: swallowing one would break the
  // FILL_IN_BLANKS player's slot substitution.
  if (value.includes('{{')) return false;
  // A leftover delimiter is punctuation, not an expression — anything paired
  // was already claimed by the delimited pass. (`\left(` is unaffected: the
  // backslash there is not immediately followed by a bracket.)
  if (STRAY_DELIMITER.test(value)) return false;
  return MATH_SAFE.test(value);
}

function trimOperatorEdges(run: Token[]): Token[] {
  let start = 0;
  let end = run.length;
  while (start < end && PURE_OPERATOR.test(run[start].value)) start++;
  while (end > start && PURE_OPERATOR.test(run[end - 1].value)) end--;
  return run.slice(start, end);
}

/**
 * Repairs the two things models get wrong often enough to matter: Unicode
 * operators KaTeX can't parse, and `3^(3x)` where `3^{3x}` was meant (bare
 * `^(` makes KaTeX superscript the parenthesis alone).
 */
function normalizeLatex(latex: string): string {
  let out = latex;
  for (const [pattern, replacement] of UNICODE_OPERATORS) {
    out = out.replace(pattern, replacement);
  }
  // Convert OCR fraction artifacts like `1 _ 2` or `3 _ 4` to `\frac{1}{2}`
  out = out.replace(/(\d+)\s*_\s*(\d+)/g, '\\frac{$1}{$2}');
  return out.replace(/([_^])\(([^()]*)\)/g, '$1{$2}');
}

// ─── DOM upgrade (web only) ─────────────────────────────────────────────────

/** Content where a backslash-paren is the literal point, so it must survive verbatim. */
const VERBATIM_ANCESTORS = `code, pre, .${MATH_CLASS}, .katex`;

/**
 * Rewrites LaTeX found in `root`'s text nodes into `.math-tex` elements, in
 * place. Call before the KaTeX pass.
 *
 * Text nodes only, and replacements are built with `createTextNode` — no
 * string-to-HTML step, so this cannot reintroduce markup DOMPurify stripped.
 */
export function upgradeRawLatex(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];

  // Collected up front: replacing nodes while the walker is mid-traversal
  // would invalidate its position.
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue ?? '';
    if (hasRawLatex(text) && !node.parentElement?.closest(VERBATIM_ANCESTORS)) {
      targets.push(node as Text);
    }
    node = walker.nextNode();
  }

  targets.forEach(upgradeTextNode);
}

function upgradeTextNode(node: Text): void {
  const text = node.nodeValue ?? '';
  const segments = findMathSegments(text);
  if (!segments.length) return;

  const fragment = document.createDocumentFragment();
  let cursor = 0;

  for (const segment of segments) {
    if (segment.start > cursor) {
      fragment.appendChild(document.createTextNode(text.slice(cursor, segment.start)));
    }

    const el = document.createElement('span');
    el.className = mathClassName(segment);
    el.setAttribute('data-latex', segment.latex);
    // The source stays as the element's text so a KaTeX failure degrades to the
    // original expression rather than to nothing.
    el.textContent = text.slice(segment.start, segment.end);
    fragment.appendChild(el);

    cursor = segment.end;
  }

  if (cursor < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(cursor)));
  }

  node.parentNode?.replaceChild(fragment, node);
}
